'use client';

import { retryAfterMs, type RetryAttemptResult } from 'elowen-plugin-shared/retry';
import { parseSse } from './sse';
import { AUTH_CLEARED_EVENT, authGeneration, subscribeAuthTransitions } from './token';

export type DaemonStreamFrame = ReturnType<typeof parseSse>['frames'][number];

export interface DaemonStreamOptions {
  signal: AbortSignal;
  /** Preserve an existing consumer ceiling; chat/global feeds currently have no JS reassembly bound. */
  maxBufferLength?: number;
  onFrame: (frame: DaemonStreamFrame) => void | Promise<void>;
  /** Includes comment-only keepalives; bytes do not imply application readiness. */
  onBytes?: (byteLength: number) => void;
  /** A complete SSE block with a valid comment line; await domain hydration before continuing. */
  onKeepalive?: () => void | Promise<void>;
  onResponse?: (response: Response) => void;
}

/** One cookie-authenticated GET and its full body lifetime, never a retrying REST helper.
 * The caller's adapter decides readiness, watchdog deadlines and whether to retry the typed outcome. */
export async function readDaemonStream(url: string, options: DaemonStreamOptions): Promise<RetryAttemptResult<void>> {
  if (options.maxBufferLength !== undefined && (!Number.isSafeInteger(options.maxBufferLength) || options.maxBufferLength <= 0)) {
    throw new TypeError('SSE reassembly bound must be a positive safe integer.');
  }
  const generation = authGeneration();
  const lifetime = new AbortController();
  const abort = () => lifetime.abort();
  const offTransition = subscribeAuthTransitions(abort);
  window.addEventListener(AUTH_CLEARED_EVENT, abort);
  options.signal.addEventListener('abort', abort, { once: true });
  if (options.signal.aborted) abort();

  let response: Response | undefined;
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  let readFailed = false;
  let cancellation: Promise<{ error: unknown } | null> | undefined;
  const cancelReader = () => {
    if (reader && !cancellation) {
      // Fetch can error its reader before this abort listener runs. Observe cleanup immediately.
      cancellation = reader.cancel().then(() => null, error => ({ error }));
    }
  };
  lifetime.signal.addEventListener('abort', cancelReader);
  const current = () => {
    if (generation !== authGeneration()) abort();
    return !lifetime.signal.aborted;
  };
  const cancelled = { outcome: 'cancelled', reason: 'aborted' } as const;

  try {
    if (!current()) return cancelled;
    try {
      response = await fetch(url, {
        method: 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'text/event-stream' }, signal: lifetime.signal,
      });
    } catch (error) {
      if (!current()) return cancelled;
      if (error instanceof TypeError) return { outcome: 'retry', reason: 'network' };
      throw error;
    }
    if (!current()) return cancelled;
    options.onResponse?.(response);
    if (!current()) return cancelled;
    if (!response.ok) {
      const status = response.status;
      if (status === 401) return { outcome: 'blocked', reason: 'auth_required' };
      if (status === 403) return { outcome: 'blocked', reason: 'forbidden' };
      if (status === 408 || status === 429 || status >= 500) {
        return {
          outcome: 'retry',
          reason: status === 408 ? 'timeout' : status === 429 ? 'rate_limited' : 'unavailable',
          retryAfterMs: retryAfterMs(response.headers.get('retry-after')),
        };
      }
      return { outcome: 'blocked', reason: 'unavailable' };
    }
    if (response.headers.get('content-type')?.split(';', 1)[0]?.trim().toLowerCase() !== 'text/event-stream') {
      throw new TypeError('Expected text/event-stream response.');
    }
    if (!response.body) throw new TypeError('SSE response has no body.');
    reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (current()) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch (error) {
        readFailed = true;
        if (!current()) return cancelled;
        if (error instanceof TypeError) return { outcome: 'retry', reason: 'network' };
        throw error;
      }
      if (!current()) return cancelled;
      if (chunk.done) return { outcome: 'retry', reason: 'closed' };
      if (!chunk.value.byteLength) continue;
      options.onBytes?.(chunk.value.byteLength);
      if (!current()) return cancelled;
      buffer += decoder.decode(chunk.value, { stream: true });
      let keepalives = 0;
      const parsed = parseSse(buffer, options.maxBufferLength, () => { keepalives += 1; });
      buffer = parsed.rest;
      for (let i = 0; i < keepalives; i += 1) {
        if (!current()) return cancelled;
        await options.onKeepalive?.();
      }
      for (const frame of parsed.frames) {
        if (!current()) return cancelled;
        await options.onFrame(frame);
      }
    }
    return cancelled;
  } finally {
    options.signal.removeEventListener('abort', abort);
    window.removeEventListener(AUTH_CLEARED_EVENT, abort);
    offTransition();
    lifetime.signal.removeEventListener('abort', cancelReader);
    if (reader) {
      try {
        if (cancellation) {
          const failure = await cancellation;
          if (failure && !(lifetime.signal.aborted && failure.error instanceof DOMException && failure.error.name === 'AbortError')) {
            throw failure.error;
          }
        } else if (!readFailed) await reader.cancel();
      } finally {
        reader.releaseLock();
      }
    } else if (response?.body) await response.body.cancel();
    lifetime.abort();
  }
}
